import { getClientTraceHeaders } from "./clientTraceHeaders";

type ApiFetchInit = RequestInit & { _retry?: boolean };

type AuthErrorPayload = {
  code?: string | null;
  reason?: string | null;
  requestId?: string | null;
};

type RefreshResult = {
  ok: boolean;
  reason?: string | null;
  code?: string | null;
  requestId?: string | null;
};

export class AuthSessionError extends Error {
  code: string | null;
  reason: string | null;
  requestId: string | null;

  constructor(
    message: string,
    details: { code?: string | null; reason?: string | null; requestId?: string | null } = {}
  ) {
    super(message);
    this.name = "AuthSessionError";
    this.code = details.code ?? null;
    this.reason = details.reason ?? null;
    this.requestId = details.requestId ?? null;
  }
}

let refreshPromise: Promise<RefreshResult> | null = null;

async function readAuthErrorPayload(response: Response): Promise<AuthErrorPayload | null> {
  try {
    const data = await response.json();
    return {
      code: typeof data?.code === "string" ? data.code : null,
      reason: typeof data?.reason === "string" ? data.reason : null,
      requestId: typeof data?.requestId === "string" ? data.requestId : null,
    };
  } catch {
    return null;
  }
}

async function tryRefresh(): Promise<RefreshResult> {
  try {
    const r = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    });
    if (r.ok) return { ok: true };
    const data = await readAuthErrorPayload(r);
    return {
      ok: false,
      reason: data?.reason || null,
      code: data?.code || null,
      requestId: data?.requestId || null,
    };
  } catch {
    return { ok: false };
  }
}

async function tryRefreshWithSoftRetry(): Promise<RefreshResult> {
  const first = await tryRefresh();
  if (first.ok) return first;
  // В Telegram/WebView cookie иногда «доезжает» с небольшой задержкой.
  // Даем один мягкий повтор перед принудительным редиректом на логин.
  if (first.reason === "refresh_cookie_missing") {
    const delayMs = 350 + Math.floor(Math.random() * 350);
    await new Promise((resolve) => window.setTimeout(resolve, delayMs));
    const second = await tryRefresh();
    if (second.ok) return second;
    return {
      ...second,
      reason: second.reason || first.reason || null,
      code: second.code || first.code || null,
      requestId: second.requestId || first.requestId || null,
    };
  }
  return first;
}

async function refreshOnce(): Promise<RefreshResult> {
  if (!refreshPromise) {
    refreshPromise = tryRefreshWithSoftRetry().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function getUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

export async function apiFetch(
  input: RequestInfo | URL,
  init: ApiFetchInit = {}
): Promise<Response> {
  const url = getUrl(input);

  // Пока другой запрос уже получил 401 и выполняет refresh, не стартуем новый fetch
  // со старыми куками — иначе параллельные miniapi-вызовы дают лишние 401 в логах.
  if (refreshPromise) {
    await refreshPromise;
  }

  const disable401Interceptor = (() => {
    try {
      if (typeof window === "undefined") return false;
      const params = new URLSearchParams(window.location.search);
      if (params.get("no401") === "1") return true;
      return localStorage.getItem("ww_disable_401_interceptor") === "1";
    } catch {
      return false;
    }
  })();

  const headers = new Headers(init.headers || undefined);
  const trace = getClientTraceHeaders();
  Object.entries(trace).forEach(([k, v]) => {
    if (!headers.has(k)) headers.set(k, v);
  });

  const response = await fetch(input, {
    ...init,
    headers,
    credentials: init.credentials ?? "include",
  });

  if (disable401Interceptor) return response;

  const isRefreshEndpoint = url.includes("/api/auth/refresh");
  if (response.status !== 401 || init._retry || isRefreshEndpoint) {
    return response;
  }

  const refreshResult = await refreshOnce();
  if (!refreshResult.ok) {
    // Fail-safe: при потере сессии всегда переводим на логин с явным маркером причины,
    // чтобы не оставлять пользователя в "белом экране" или бесконечной загрузке.
    const p = window.location.pathname;
    if (p !== "/login" && p !== "/registration") {
      if (refreshResult.reason === "active_session_not_found") {
        window.location.assign("/login?session=active_session_not_found");
        throw new AuthSessionError("Unauthorized", {
          reason: refreshResult.reason || null,
          code: refreshResult.code || null,
          requestId: refreshResult.requestId || null,
        });
      }
      window.location.assign("/login?session=expired");
    }
    throw new AuthSessionError("Unauthorized", {
      reason: refreshResult.reason || null,
      code: refreshResult.code || null,
      requestId: refreshResult.requestId || null,
    });
  }

  return apiFetch(input, { ...init, _retry: true });
}

